/** Standard GST tax-invoice / POS receipt HTML for print. */

function esc(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function inr(n) {
    return `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDateTime(iso) {
    return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
}

const PAYMENT_LABELS = {
    cash: "Cash",
    upi: "UPI",
    card: "Card",
    razorpay: "Razorpay",
    wallet: "Wallet",
};

/**
 * @param {object} invoice — full invoice from API
 * @param {object} business — { name, address, city, phone, gstin }
 */
export function buildReceiptPrintHtml(invoice, business = {}) {
    const cust = invoice.customer || {};
    const items = invoice.items || [];
    const bizName = business.name || "Salon & Spa";
    const addressLine = [business.address, business.city].filter(Boolean).join(", ");
    const gstin = business.gstin?.trim();
    const phone = business.phone?.trim();
    const cgst = invoice.cgst || 0;
    const sgst = invoice.sgst || 0;
    const discount = invoice.discount || 0;
    const payment = PAYMENT_LABELS[invoice.payment_mode] || invoice.payment_mode || "—";

    const itemRows = items.map((it, i) => {
        const line = it.unit_price * it.qty;
        return `
            <tr>
                <td class="col-sn">${i + 1}</td>
                <td class="col-item">${esc(it.name)}</td>
                <td class="col-qty">${it.qty}</td>
                <td class="col-amt">${inr(line)}</td>
            </tr>`;
    }).join("");

    const metaRows = [
        ["Invoice No.", esc(invoice.invoice_number)],
        ["Date & Time", esc(fmtDateTime(invoice.created_at))],
        ["Customer", esc(cust.full_name || "Walk-in Guest")],
        ["Mobile", esc(cust.mobile || "—")],
    ].map(([k, v]) => `<div class="meta-row"><span class="meta-k">${k}</span><span class="meta-v">${v}</span></div>`).join("");

    const totalRows = [
        ["Sub-total", inr(invoice.sub_total)],
        cgst > 0 ? ["CGST", inr(cgst)] : null,
        sgst > 0 ? ["SGST", inr(sgst)] : null,
        discount > 0 ? ["Discount", `−${inr(discount)}`] : null,
    ].filter(Boolean).map(([k, v]) => `
        <div class="sum-row"><span>${k}</span><span>${v}</span></div>`).join("");

    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>${esc(invoice.invoice_number)}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @page { size: 80mm auto; margin: 4mm; }
        body {
            font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
            font-size: 11px;
            line-height: 1.45;
            color: #1a1a1a;
            background: #fff;
            padding: 8px;
        }
        .receipt {
            max-width: 72mm;
            margin: 0 auto;
        }
        /* ── Header ── */
        .head {
            text-align: center;
            padding-bottom: 10px;
            border-bottom: 2px solid #1a1a1a;
        }
        .biz-name {
            font-size: 15px;
            font-weight: 800;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: #4A0E17;
            margin-bottom: 4px;
        }
        .biz-meta {
            font-size: 9.5px;
            color: #444;
            line-height: 1.5;
        }
        .doc-type {
            margin-top: 8px;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            color: #333;
        }
        /* ── Invoice meta ── */
        .meta {
            padding: 10px 0;
            border-bottom: 1px dashed #999;
        }
        .meta-row {
            display: flex;
            justify-content: space-between;
            gap: 8px;
            padding: 2px 0;
            font-size: 10px;
        }
        .meta-k { color: #666; flex-shrink: 0; }
        .meta-v { font-weight: 600; text-align: right; }
        /* ── Items table ── */
        table.items {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
            font-size: 10px;
        }
        table.items thead th {
            border-top: 1px solid #333;
            border-bottom: 1px solid #333;
            padding: 5px 2px;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #333;
        }
        table.items tbody td {
            padding: 5px 2px;
            vertical-align: top;
            border-bottom: 1px dotted #ccc;
        }
        .col-sn { width: 16px; text-align: center; color: #888; }
        .col-item { text-align: left; }
        .col-qty { width: 24px; text-align: center; }
        .col-amt { width: 56px; text-align: right; font-weight: 600; white-space: nowrap; }
        /* ── Totals ── */
        .totals {
            border-top: 1px solid #333;
            padding-top: 6px;
            margin-bottom: 8px;
        }
        .sum-row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
            font-size: 10px;
        }
        .grand {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 6px;
            padding: 8px 0;
            border-top: 2px solid #1a1a1a;
            border-bottom: 2px solid #1a1a1a;
            font-size: 13px;
            font-weight: 800;
        }
        .pay-row {
            text-align: center;
            font-size: 10px;
            font-weight: 700;
            padding: 6px 0;
            text-transform: uppercase;
            letter-spacing: 0.08em;
        }
        /* ── Footer ── */
        .foot {
            text-align: center;
            padding-top: 10px;
            border-top: 1px dashed #999;
            font-size: 9px;
            color: #555;
            line-height: 1.6;
        }
        .foot-thanks {
            font-size: 11px;
            font-weight: 700;
            color: #4A0E17;
            margin-bottom: 4px;
        }
        @media print {
            body { padding: 0; }
            .receipt { max-width: 100%; }
        }
    </style>
</head>
<body>
    <div class="receipt">
        <header class="head">
            <div class="biz-name">${esc(bizName)}</div>
            ${addressLine ? `<div class="biz-meta">${esc(addressLine)}</div>` : ""}
            ${phone || gstin ? `<div class="biz-meta">${phone ? `Tel: ${esc(phone)}` : ""}${phone && gstin ? " · " : ""}${gstin ? `GSTIN: ${esc(gstin)}` : ""}</div>` : ""}
            <div class="doc-type">Tax Invoice</div>
        </header>

        <section class="meta">${metaRows}</section>

        <table class="items">
            <thead>
                <tr>
                    <th class="col-sn">#</th>
                    <th class="col-item">Description</th>
                    <th class="col-qty">Qty</th>
                    <th class="col-amt">Amount</th>
                </tr>
            </thead>
            <tbody>${itemRows || '<tr><td colspan="4" style="text-align:center;padding:12px">No items</td></tr>'}</tbody>
        </table>

        <section class="totals">
            ${totalRows}
            <div class="grand">
                <span>Grand Total</span>
                <span>${inr(invoice.grand_total)}</span>
            </div>
            <div class="pay-row">Paid via ${esc(payment)}</div>
        </section>

        <footer class="foot">
            <div class="foot-thanks">Thank you for visiting us!</div>
            <div>We look forward to serving you again.</div>
            <div style="margin-top:6px">This is a computer-generated invoice and does not require a signature.</div>
        </footer>
    </div>
    <script>window.onload=function(){window.print();}<\/script>
</body>
</html>`;
}

export function openReceiptPrint(invoice, business = {}) {
    const w = window.open("", "_blank", "width=420,height=720");
    if (!w) return false;
    w.document.write(buildReceiptPrintHtml(invoice, business));
    w.document.close();
    return true;
}

/** Download receipt as HTML (open in browser or save for PDF). */
export function downloadReceipt(invoice, business = {}) {
    const html = buildReceiptPrintHtml(invoice, business).replace(
        /<script>[\s\S]*?<\/script>/i,
        "",
    );
    const safeName = String(invoice.invoice_number || "receipt").replace(/[/\\?%*:|"<>]/g, "-");
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}.html`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 3000);
    return true;
}
